import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@yhelp/ui';
import { useAuth } from '@/auth';
import { useProductTour } from '@/components/organisms/ProductTour/ProductTour';
import {
  isTourCompleted,
  toursForRole,
  type OnboardingPrefs,
} from '@/components/organisms/ProductTour/tour-definitions';
import { useMe } from '@/hooks/useMe';

function TourIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path
        d="M9 20l-5.447-2.724A2 2 0 013 15.382V6.618a2 2 0 011.553-1.894L9 2m0 18l6-3m-6 3V2m6 15l5.447-2.724A2 2 0 0021 12.382V3.618a2 2 0 00-1.553-1.894L15 0m0 17V0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function HelpFab({ chatOpen = false }: { chatOpen?: boolean }) {
  const [open, setOpen] = useState(false);
  const { startTour, isActive } = useProductTour();
  const { user } = useAuth();
  const { data: me } = useMe();
  const onboarding = (me?.data.user as { preferences?: { onboarding?: OnboardingPrefs } })
    ?.preferences?.onboarding;
  const role = user?.role ?? 'requester';
  const availableTours = toursForRole(role);

  if (isActive) return null;

  const fabClass = chatOpen
    ? 'bottom-24 right-4 sm:bottom-28 sm:right-6'
    : 'bottom-4 right-4 sm:bottom-6 sm:right-6';
  const panelClass = chatOpen
    ? 'bottom-[7.5rem] right-4 sm:bottom-[8.5rem] sm:right-6'
    : 'bottom-[4.5rem] right-4 sm:bottom-[5.5rem] sm:right-6';

  return (
    <>
      <button
        type="button"
        data-tour="help-fab"
        aria-expanded={open}
        aria-label={open ? 'Fechar ajuda' : 'Abrir ajuda e tours'}
        onClick={() => setOpen((v) => !v)}
        className={`fixed z-40 flex h-12 w-12 items-center justify-center rounded-full border-2 border-yhelp-black bg-white text-lg font-extrabold text-yhelp-black shadow-brand transition-transform duration-fast hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100 dark:border-yhelp-yellow dark:bg-neutral-900 dark:text-yhelp-yellow ${fabClass}`}
      >
        ?
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Central de ajuda"
          className={`fixed z-50 w-[min(20rem,calc(100vw-2rem))] animate-fade-in-up rounded-2xl border border-neutral-200 bg-white p-4 shadow-brand motion-reduce:animate-none dark:border-neutral-700 dark:bg-neutral-900 ${panelClass}`}
        >
          <h3 className="font-bold text-yhelp-black dark:text-white">Ajuda e tours</h3>
          <p className="mt-1 text-sm text-muted">Escolha um tour guiado por funcionalidade.</p>
          <div className="mt-3 max-h-64 space-y-1 overflow-y-auto">
            {availableTours.map((tour) => {
              const done = isTourCompleted(onboarding, tour.id);
              return (
                <button
                  key={tour.id}
                  type="button"
                  className="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => {
                    setOpen(false);
                    startTour(tour.id);
                  }}
                >
                  <TourIcon />
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold text-yhelp-black dark:text-white">
                      {tour.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted">{tour.description}</span>
                  </span>
                  {done ? <span className="shrink-0 text-xs text-status-resolvido">✓</span> : null}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-col gap-2 border-t border-neutral-200 pt-3 dark:border-neutral-700">
            <Link to="/kb" onClick={() => setOpen(false)}>
              <Button size="sm" variant="secondary" className="w-full justify-start gap-2">
                <BookIcon />
                Base de conhecimento
              </Button>
            </Link>
          </div>
        </div>
      ) : null}
    </>
  );
}
